import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { subscriptionApi } from '@/services/api'
import { captureSignupAttribution, readSignupAttribution } from '@/utils/signupAttribution'
import type { PromotionSummary } from '@/types/promotions'

export interface ProductHuntOffer {
    id: string
    percentage: number
    intervals: ('monthly' | 'annually')[]
    /** Guests: the trial length the offer must be paid within. */
    trialDays?: number
    /** Accounts: their own deadline (the promo's end or their trial's end, whichever is first). */
    deadline?: string
    /** The full summary for accounts, for passing straight to the upgrade modal. */
    promotion?: PromotionSummary
}

/**
 * The Product Hunt offer this visitor can still use, or null.
 *
 * Guests: only when the visit is attributed to Product Hunt and an offer is
 * running. Accounts: only when the server says this account is still eligible
 * (signup source, never paid, inside its trial window). One source of truth
 * for the public banner, the register page and the in-app trial strip.
 */
export function useProductHuntOffer(): ProductHuntOffer | null {
    const { isAuthenticated, isLoading } = useAuth()
    const { search } = useLocation()
    // Accounts are judged by the server, not the URL; don't refetch on every in-app navigation.
    const attributionSearch = isAuthenticated ? '' : search
    const [offer, setOffer] = useState<ProductHuntOffer | null>(null)

    useEffect(() => {
        if (isLoading) return
        let active = true
        // App records attribution in its own effect, which runs after this child's;
        // record it here too so the very first visit from Product Hunt counts.
        captureSignupAttribution({ search: attributionSearch }, document.referrer)
        const load: Promise<ProductHuntOffer | null> = isAuthenticated
            ? subscriptionApi.getPromotions().then(({ data }) => {
                const p = data?.[0]
                return p ? { id: p.id, percentage: p.percentage, intervals: p.intervals, deadline: p.expires_at, promotion: p } : null
            })
            : readSignupAttribution().signup_source === 'producthunt'
                ? subscriptionApi.getCurrentOffer().then(({ data }) =>
                    data ? { id: data.id, percentage: data.percentage, intervals: data.intervals, trialDays: data.trial_days } : null)
                : Promise.resolve(null)
        load.then((next) => { if (active) setOffer(next) }).catch(() => { if (active) setOffer(null) })
        return () => { active = false }
    }, [isAuthenticated, isLoading, attributionSearch])

    return offer
}

/** "month", "year" or "month or year", for the offer's copy. */
export function offerPeriodKey(intervals: ProductHuntOffer['intervals']): string {
    if (intervals.length > 1) return 'promoBanner.periodBoth'
    return intervals[0] === 'annually' ? 'promoBanner.periodYear' : 'promoBanner.periodMonth'
}
